import {
  ActionIcon,
  Box,
  Loader,
  Menu,
  ScrollArea,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { IconEye, IconPencil, IconTrash } from "@tabler/icons-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteNoteDraft, getAllDrafts } from "../../services/note";
import { setDrafts, setDraftsLoading } from "../../store/reducers/note";
import swal from "sweetalert";
import NoNoteDataSvg from "../Svgs/NoNoteDataSvg";
import CustomPagination from "../Pagination/CustomPagination";

export const draftPaginationStyles = {
  control: {
    minWidth: 30,
    height: 30,
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    borderRadius: 7,
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    fontWeight: 400,
    "&[data-active]": {
      backgroundColor: "var(--app-primary-soft, #FFF6E9)",
      borderColor: "var(--app-primary-bd, #FFE2B8)",
      color: "#B4700A",
      fontWeight: 700,
    },
  },
};

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    transition: "box-shadow 150ms ease",
    "& th": {
      backgroundColor: "var(--app-surface-2) !important",
      color: "var(--app-text-faint, #8A93A2) !important",
      fontSize: "11px !important",
      fontWeight: "700 !important",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "11px 16px !important",
      borderBottom: "1px solid var(--app-border) !important",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
  scrolled: {
    boxShadow: theme.shadows.sm,
  },
  scrollArea: {
    // backgroundColor: "var(--app-surface)",
    // borderRadius: "12px",
    // border: "1px solid var(--app-border)",
    // boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    "& tbody tr:hover td": {
      backgroundColor: "var(--app-surface-2) !important",
    },
    "& tbody tr:last-child td": {
      borderBottom: "none !important",
    },
  },
  loading: {
    height: "100dvh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  noData: {
    padding: "2rem",
    textAlign: "center",
    height: "100dvh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  paginationFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid var(--app-border)",
    fontSize: 12.5,
    color: "var(--app-text-muted)",
  },
  bg1: {
    "& td": {
      background: "var(--app-surface) !important",
      borderBottom: "1px solid var(--app-border) !important",
      padding: "13px 16px !important",
      fontSize: "13.5px !important",
    },
    cursor: "pointer",
  },
  bg2: {
    "& td": {
      background: "var(--app-surface) !important",
      borderBottom: "1px solid var(--app-border) !important",
      padding: "13px 16px !important",
      fontSize: "13.5px !important",
    },
    cursor: "pointer",
  },
  textDark: {
    "&&": {
      color: "var(--app-text) !important",
    },
  },
  textMuted: {
    "&&": {
      color: "var(--app-text-muted) !important",
    },
  },
  kebab: {
    "&&": {
      width: "30px !important",
      height: "30px !important",
      minHeight: "30px !important",
      minWidth: "30px !important",
      borderRadius: "8px !important",
      border: "1px solid transparent !important",
      background: "none !important",
      color: "var(--app-text-faint, #8A93A2) !important",
      cursor: "pointer",
      display: "grid",
      placeItems: "center",
      "&:hover": {
        background: "var(--app-surface-2) !important",
        borderColor: "var(--app-border) !important",
        color: "var(--app-text) !important",
      },
    },
  },
}));

const ITEMS_PER_PAGE = 10;

const DraftNotesTab = ({ userId, onEditDraft, onViewDraft }) => {
  const dispatch = useDispatch();
  const { drafts, draftsLoading, draftSavedAt } = useSelector((state) => state.note);
  const { allPatients } = useSelector((state) => state?.patient);
  const { allStaff } = useSelector((state) => state?.staff);
  const { classes, cx } = useStyles();
  const [scrolled, setScrolled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    getAllDrafts(dispatch, userId || null);
  }, [userId]);

  // Re-fetch silently (no loading flash) whenever a draft is auto-saved
  useEffect(() => {
    if (draftSavedAt > 0) {
      getAllDrafts(dispatch, userId || null, false);
    }
  }, [draftSavedAt]);

  // Clamp page when drafts shrink (e.g. after delete)
  const totalPages = Math.ceil((drafts?.length || 0) / ITEMS_PER_PAGE);
  const safePage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const pagedDrafts = (drafts || []).slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  // For Incident Report, `draft.client` is an array of resolved patient
  // objects (one per involved client) — any single one is enough to find
  // and delete the same draft (the backend matches by membership).
  const firstClient = (draft) => (Array.isArray(draft.client) ? draft.client[0] : draft.client);

  const handleDelete = async (draft) => {
    const confirmed = await swal({
      title: "Delete Draft?",
      text: "This draft will be permanently deleted and cannot be recovered.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (!confirmed) return;

    dispatch(setDraftsLoading(true));
    const clientValue = firstClient(draft);
    await deleteNoteDraft(draft.noteType?._id || draft.noteType, clientValue?._id || clientValue);
    dispatch(setDrafts(drafts.filter((d) => d._id !== draft._id)));
  };

  // `client` comes back from GET /drafts already resolved to
  // {name, firstName, lastName} objects — single for every note type
  // except Incident Report, where it's an array (multi-client). `staff`
  // (the draft owner) is always a single resolved object.
  const getClientName = (draft) => {
    if (Array.isArray(draft.client)) {
      const names = draft.client.map((c) => c?.name).filter(Boolean);
      return names.length ? names.join(", ") : "—";
    }
    return draft.client?.name || "—";
  };

  // Multi-staff selections (Incident Report's staffInvolved, or the newer
  // multiStaffIds for Essential Support/Billing Services/Intervention
  // Progress/Progress Note) live in formState — not the fields["Staff Name"]
  // scalar, which is only ever populated at actual Save time — so a draft
  // saved with multiple staff selected must resolve them from there too.
  const getStaffName = (draft) => {
    const multiIds =
      draft.formState?.multiStaffIds?.length
        ? draft.formState.multiStaffIds
        : draft.formState?.incidentDetails?.staffInvolved?.length
        ? draft.formState.incidentDetails.staffInvolved
        : null;
    if (multiIds?.length) {
      const names = multiIds
        .map((id) => allStaff?.find((s) => s?.id === id || s?._id === id)?.name)
        .filter(Boolean);
      if (names.length) return names.join(", ");
    }
    // The form's own "Staff Name" field selection (who this note credits)
    // takes priority when present; falls back to the draft owner otherwise.
    const staffId = draft.formState?.fields?.["Staff Name"];
    if (staffId) {
      const staff = allStaff?.find((s) => s?.id === staffId || s?._id === staffId);
      if (staff?.name) return staff.name;
    }
    return draft.staff?.name || "—";
  };


  return (
    <Box>
      <ScrollArea
        onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
        className={classes.scrollArea}
      >
        {draftsLoading ? (
          <Box className={classes.loading}>
            <Loader color="yellow" />
          </Box>
        ) : drafts?.length > 0 ? (
          <Table
            fontSize="sm"
            enableStickyHeader
            verticalSpacing="md"
            sx={{ fontFamily: "Inter" }}
          >
            <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
              <tr>
                <th style={{ minWidth: "140px" }}>Id</th>
                <th style={{ minWidth: "200px" }}>Note Type</th>
                <th style={{ minWidth: "180px" }}>Staff Name</th>
                <th style={{ minWidth: "140px" }}>Facility Name</th>
                <th style={{ minWidth: "140px" }}>Client Name</th>
                <th style={{ minWidth: "180px" }}>Last Saved</th>
                <th style={{ minWidth: "170px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedDrafts.map((draft, i) => {
                const bg = i % 2 === 0;
                const handleRowClick = () => onViewDraft?.(draft);
                return (
                  <tr key={draft._id} className={bg ? classes.bg2 : classes.bg1}>
                    <td className={classes.textMuted} onClick={handleRowClick} style={{ textTransform: "uppercase" }}>
                      {draft._id?.slice(-6)}
                    </td>
                    <td className={classes.textDark} onClick={handleRowClick}>{draft.noteType?.name || "—"}</td>
                    <td className={classes.textMuted} onClick={handleRowClick}>{getStaffName(draft)}</td>
                    <td className={classes.textMuted} onClick={handleRowClick}>{draft.facility?.name || "—"}</td>
                    <td className={classes.textDark} onClick={handleRowClick}>{getClientName(draft)}</td>
                    <td className={classes.textMuted} onClick={handleRowClick}>{moment(draft.updatedAt).format("MM/DD/YYYY hh:mm A")}</td>
                    <td>
                      <Menu shadow="md" position="bottom-end" withinPortal zIndex={1500}>
                        <Menu.Target>
                          <ActionIcon className={classes.kebab}>
                            <BiDotsVerticalRounded size={18} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item icon={<IconEye size={16} />} onClick={handleRowClick}>
                            View
                          </Menu.Item>
                          <Menu.Item icon={<IconPencil size={16} />} onClick={() => onEditDraft?.(draft)}>
                            Continue Editing
                          </Menu.Item>
                          <Menu.Item
                            color="red"
                            icon={<IconTrash size={16} />}
                            onClick={() => handleDelete(draft)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <Box className={classes.noData}>
            <NoNoteDataSvg />
            <Text fw={600} size="xl" mb="md" c="var(--app-text)" ff="inter">
              No Saved Drafts
            </Text>
            <Text fw={400} size="md" c="var(--app-text-faint, #8A93A2)">
              Drafts are automatically saved as you work and will appear here
              until you submit or discard them.
            </Text>
          </Box>
        )}
      </ScrollArea>

      {!draftsLoading && drafts?.length > ITEMS_PER_PAGE && (
        <CustomPagination
          data={drafts.length}
          itemsPerPage={ITEMS_PER_PAGE}
          currentPage={safePage}
          handlePageChange={(page) => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          showSummary
          className={classes.paginationFooter}
          paginationStyles={draftPaginationStyles}
        />
      )}
    </Box>
  );
};

export default DraftNotesTab;
